/**
 * Names and constants for BCEcloud's internal soft launch.
 *
 * BCEcloud runs alongside the classic portal while internal staff try it out.
 * Everything that expresses that transition — where "classic" is, what domain
 * people should save, which app this is when reporting adoption — is named
 * here so the copy, the account menu and the telemetry cannot drift apart, and
 * so the transitional half is deleted in one place when the launch ends.
 *
 * The user-facing copy deliberately avoids calling this "the new BCEcloud".
 * These screens outlive the launch, and a product that introduces itself as
 * "new" reads as stale the moment it isn't. What is genuinely temporary — that
 * it is staff-only, that the classic portal is still the system of record —
 * lives in the sign-in notice, not in the name.
 */

/** The classic (Heroku) BCEcloud admin portal, still the system of record. */
export const LEGACY_PORTAL_URL = 'https://www.bcecloud.com';

/** Human-readable label for the classic portal, used in links and copy. */
export const LEGACY_PORTAL_LABEL = 'Classic BCEcloud';

/** The product's name, as it is written wherever a person reads it. */
export const APP_NAME = 'BCEcloud';

/** The domain people should bookmark for the admin. */
export const APP_DOMAIN = 'app.bcecloud.com';

/**
 * The customer-facing My Events portal, for view-as-customer links. In dev
 * the portal runs beside this app, so preview links must not jump to prod;
 * the dev port is overridable for worktree setups running shifted ports.
 */
export const MYEVENTS_PORTAL_URL = import.meta.env.DEV
  ? (import.meta.env['VITE_MYEVENTS_PORTAL_URL'] as string | undefined) ?? 'http://localhost:3001'
  : 'https://app.mybcecloud.com';

/**
 * How this app identifies itself to the adoption endpoint. The classic portal
 * reports `legacy` against the same endpoint once it is instrumented, which is
 * what makes the two comparable.
 */
export const APP_ID = 'app';
